
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { CreditCard } from 'lucide-react';
import { StripeConfigDialog } from './StripeConfigDialog';
import { toast } from 'sonner';

interface StripePaymentMethodProps {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  disabled: boolean;
  shopId: string;
}

export const StripePaymentMethod: React.FC<StripePaymentMethodProps> = ({
  enabled,
  onToggle,
  disabled,
  shopId
}) => {
  const [showStripeConfig, setShowStripeConfig] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Vérifier si les identifiants Stripe existent déjà
    const checkStripeCredentials = async () => {
      try {
        setIsChecking(true);
        const response = await fetch(`/api/payment-credentials?shopId=${shopId}&provider=stripe`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        const data = await response.json();
        setHasCredentials(data.hasCredentials || false);
      } catch (error) {
        console.error('Erreur lors de la vérification des identifiants Stripe:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkStripeCredentials();
  }, [shopId, showStripeConfig]);

  const handleToggle = (checked: boolean) => {
    if (checked && !hasCredentials) {
      // Si l'utilisateur essaie d'activer sans avoir configuré les identifiants
      toast.error('Veuillez configurer vos identifiants Stripe avant d\'activer ce mode de paiement');
      setShowStripeConfig(true);
      return;
    }
    
    // Autoriser la désactivation même si les identifiants sont configurés
    onToggle(checked);
  };

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-purple-100">
            <CreditCard className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-medium">Stripe</h3>
            <p className="text-sm text-muted-foreground">
              Acceptez les paiements par carte de crédit via Stripe
            </p>
          </div>
        </div>
        <div className="flex space-x-2 items-center">
          <Button 
            variant="outline" 
            size="sm"
            className="whitespace-nowrap"
            onClick={() => setShowStripeConfig(true)}
          >
            Configurer
          </Button>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={disabled || (isChecking && !hasCredentials)}
          />
        </div>
      </div>
      <StripeConfigDialog 
        shopId={shopId}
        open={showStripeConfig}
        onOpenChange={setShowStripeConfig}
        onSaved={() => setHasCredentials(true)}
      />
    </div>
  );
};
